import * as React from 'react';
import { observer } from 'mobx-react';
import { DecisionDispatcher } from 'nomst/Stores/Decision/decision.dispatcher';
import { ManagerWorkflowStore } from 'nomst/Stores/Decision/managerWorkflow.store';
import { WorkflowAction } from 'nomst/Stores/Decision/Constants/workflowAction';
import { User } from 'nomst/Stores/User/user';
import { buildClassString } from '@payscale/payscale-shared-ui/lib/StringUtils';
import { Tooltip } from '@payscale/design';
import coalesce from 'nomst/Stores/Utility/coalesce';
import { ManagerWorkflow as MWF } from 'nomst/Stores/Decision/managerWorkflow';
import { WorkflowSupportHeader } from './workflowSupportHeader';
import { ReportsView, ReviewType, FeatureFlags } from 'typings/enums';
import { managerListSidePanelStore, cycleRules } from 'utils/MobxDataManager/singletons';
import { PrintPdfModal } from 'nomst/Components/Decision/printPdfModal';
import { SendForReviewButton} from 'nomst/Components/Decision/sendForReviewButton';
import { featureFlagHelper } from 'singletons';
import './managerWorkflow.scss';

const placeholderReviewerName = 'XXXXX XXXXXX';

export interface IManagerWorkflowProps {
    decisionDispatcher: DecisionDispatcher;
    managerWorkflowStore: ManagerWorkflowStore;
    currentUser: User;
    multiManager: boolean;
    teamView: ReportsView;
    pdfPrintStatus: IPrintPollStatus;
}

interface IManagerWorkflowState {
    printPdfModalOpen: boolean;
}

const maxDisplayedReviewers = 30;
export class ManagerWorkflow extends React.Component<IManagerWorkflowProps, IManagerWorkflowState> {
    constructor(props: IManagerWorkflowProps) {
        super(props);

        this.state = {
            printPdfModalOpen: false
        };

        this.openPdfModal = this.openPdfModal.bind(this);
        this.closePdfModal = this.closePdfModal.bind(this);
        this.handleApprove = this.handleApprove.bind(this);
        this.handleSendBack = this.handleSendBack.bind(this);
        this.handleSubmitForReview = this.handleSubmitForReview.bind(this);
        this.printPdf = this.printPdf.bind(this);
        this.resetPdfPrintStatus = this.resetPdfPrintStatus.bind(this);
    }

    render(): React.ReactNode {
        const { teamView } = this.props;
        if (teamView === ReportsView.AllReports) {
            return null;
        }
        return (
            <div className="nomst-manager-workflow-header">
                <PrintPdfModal
                    closePdfModal={this.closePdfModal}
                    pdfModalVisible={this.state.printPdfModalOpen}
                    printPdf={this.printPdf}
                    pdfPrintStatus={this.props.pdfPrintStatus}
                    resetState={this.resetPdfPrintStatus}
                />
                {this.renderWorkflowButtons()}
                {this.renderReviewStatus()}
            </div>
        );
    }

    private renderWorkflowButtons(): React.ReactNode {
        const { currentUser, managerWorkflowStore } = this.props;
        const { selectedManagerWorkflows } = managerWorkflowStore;

        let supportUserControl: React.ReactNode = null;

        if (currentUser.isSupportOnly) {
            supportUserControl = (
                <WorkflowSupportHeader
                    managerWorkflowStore={managerWorkflowStore}
                    user={currentUser}
                />
            );
        }

        if (cycleRules.isManagerWorkflowLocked) {
            return (
                <>
                    {supportUserControl}
                    {this.showPdfDownloadButton() &&
                        <button
                            type="button"
                            className={this.buildButtonClassString('pdf-publish-btn', false)}
                            onClick={this.openPdfModal}
                        >
                            Download Comp Memos
                        </button>
                    }
                    <span className="workflow-locked">
                        <i className="icon-lock" /> Team is locked for review
                    </span>

                </>
            );
        }

        if (!selectedManagerWorkflows || !managerListSidePanelStore.allManagerIds.length) {
            return (
                <div className="workflow-buttons">
                    {supportUserControl}
                    <button
                        disabled={true}
                        className={this.buildButtonClassString('approve-btn', false)}
                    >
                        Approve
                    </button>
                    <button
                        disabled={true}
                        className={this.buildButtonClassString('review-btn', true)}
                    >
                        Send for Review
                    </button>
                </div>
            );
        }

        return (
            <div className="workflow-buttons">
                <button
                    hidden={!this.isApproveButtonVisible}
                    className={this.buildButtonClassString('approve-btn', false)}
                    onClick={this.handleApprove}
                >
                    Approve
                </button>
                <button
                    hidden={!this.isSendBackButtonVisible}
                    className={this.buildButtonClassString('send-back-btn', false)}
                    onClick={this.handleSendBack}
                >
                    Send Back
                </button>
                <SendForReviewButton
                    managerWorkflowStore={managerWorkflowStore}
                    decisionDispatcher={this.props.decisionDispatcher}
                    user={currentUser}
                />
            </div>
        );
    }

    private openPdfModal(): void {
        this.setState({printPdfModalOpen: true});
    }

    private closePdfModal(): void {
        this.setState({printPdfModalOpen: false});
    }

    private showPdfDownloadButton(): boolean {
        const { multiManager, teamView } = this.props;
        return cycleRules.isCurrentCyclePublished &&
            !multiManager &&
            teamView === ReportsView.DirectsOnly &&
            !this.isManagerAndCompMemoDisabled() && // if they are a manager and comp memo is disabled then don't show by setting false
            featureFlagHelper.featureFlagSettings[FeatureFlags.ShowCompMemo] as boolean;
    }

    private isManagerAndCompMemoDisabled(): boolean {
        return this.props.currentUser.isRoleOnlyManager &&
            this.props.managerWorkflowStore.isCompMemoDisabled();
    }

    private async printPdf(): Promise<void> {
        await this.props.decisionDispatcher.dispatchPdfPrintServiceCall();
    }

    private resetPdfPrintStatus(): void {
        this.props.decisionDispatcher.dispatchResetPrintStatus();
    }

    private handleApprove(): void {
        const { decisionDispatcher } = this.props;
        decisionDispatcher.dispatchWorkflowAction(WorkflowAction.Approve);
    }

    private handleSendBack(): void {
        const { decisionDispatcher } = this.props;
        decisionDispatcher.dispatchWorkflowAction(WorkflowAction.SendBack);
    }

    private handleSubmitForReview(): void {
        const { decisionDispatcher } = this.props;
        decisionDispatcher.dispatchWorkflowAction(WorkflowAction.SubmitReview);
    }

    private get isApproveButtonVisible(): boolean {
        const { currentUser, managerWorkflowStore } = this.props;
        const { selectedManagerWorkflows } = managerWorkflowStore;

        if (!selectedManagerWorkflows || !selectedManagerWorkflows.length) {
            return false;
        }

        if (currentUser.isSupportPersonnel) {
            return true;
        }

        for (const managerWorkflow of selectedManagerWorkflows) {
            const currentUserAsReviewerIndex = managerWorkflowStore
                .getCurrentUserAsReviewerIndex(managerWorkflow, currentUser);
            const currentUserIsLastInReviewerChain =
                currentUserAsReviewerIndex === coalesce.array(managerWorkflow.reviewers).length - 1;

            if (!currentUserIsLastInReviewerChain) {
                return false;
            }

        }

        return true;
    }

    private get isSendBackButtonVisible(): boolean {
        const { currentUser, managerWorkflowStore } = this.props;
        const { selectedManagerWorkflows } = managerWorkflowStore;

        if (!selectedManagerWorkflows || !selectedManagerWorkflows.length) {
            return false;
        }

        for (const managerWorkflow of selectedManagerWorkflows) {
            const currentReviewerIsFirstReviewer = managerWorkflow.currentReviewerIndex === 0;
            const currentUserAsReviewerIndex =
                managerWorkflowStore.getCurrentUserAsReviewerIndex(managerWorkflow, currentUser);
            const userIsCurrentReviewerOrAfter =
                currentUserAsReviewerIndex >= managerWorkflow.currentReviewerIndex;
            if (currentReviewerIsFirstReviewer || !(currentUser.isHrAssistant || userIsCurrentReviewerOrAfter)) {
                return false;
            }
        }

        return true;
    }

    private buildButtonClassString(className: string, isPrimary: boolean): string {
        return buildClassString({
            [className]: !!className,
            'pxl-btn': true,
            'pxl-btn--sm': true,
            'pxl-btn--primary': isPrimary
        });
    }

    private renderReviewStatus(): React.ReactNode {
        const { selectedManagerWorkflows } = this.props.managerWorkflowStore;
        const { multiManager } = this.props;
        const managerWorkflow = selectedManagerWorkflows && selectedManagerWorkflows[0];
        const placeholderClass = this.needsPlaceholder(managerWorkflow) ? 'placholder-reviewer' : '';
        if (managerWorkflow && managerWorkflow.isApproved) {
            if (multiManager) {
                const listOfApprovers = this.getSelectedReviewerNames(ReviewType.Approved);
                return (
                    <div className="review-status">
                        <span className="approved-review-status">
                            <span className="reviewer-text">
                                Approved By&nbsp;
                            </span>
                            <span className="reviewer-name">
                                <Tooltip
                                    className="reviewer-name__tooltip reviewer-name__tooltip--approved"
                                    display="underline"
                                    hoverText={listOfApprovers}
                                    id="reviewer-name-approved"
                                    position="left"
                                    tooltipText="Multiple"
                                />
                            </span>
                        </span>
                    </div>
                );
            } else {
                return (
                    <div className="review-status">
                        <span className="approved-review-status">
                            <span className="reviewer-text">
                                Approved By&nbsp;
                            </span>
                            <span className="reviewer-name">
                                {managerWorkflow.approvedByUserDisplayName}
                            </span>
                        </span>
                    </div>
                );
            }
        }
        if (multiManager) {
            const listOfCurrentReviewers = this.getSelectedReviewerNames(ReviewType.Current);
            const listOfNextReviewers = this.getSelectedReviewerNames(ReviewType.Next);

            return (
                <div className="review-status">
                    <span className="current-reviewer-status">
                        <span className="reviewer-text">
                             Current Reviewer&nbsp;
                        </span>
                        <span className={`reviewer-name ${placeholderClass}`}>
                            <Tooltip
                                className="reviewer-name__tooltip reviewer-name__tooltip--current"
                                display="underline"
                                hoverText={listOfCurrentReviewers}
                                id="reviewer-name-current"
                                position="left"
                                tooltipText="Multiple"
                            />
                        </span>
                    </span>
                    <span className="next-reviewer-status">
                        <span className="reviewer-text">
                            Next Reviewer&nbsp;
                        </span>
                        <span className={`reviewer-name ${placeholderClass}`}>
                            <Tooltip
                                className="reviewer-name__tooltip reviewer-name__tooltip--next"
                                display="underline"
                                hoverText={listOfNextReviewers}
                                id="reviewer-name-next"
                                position="left"
                                tooltipText="Multiple"
                            />
                        </span>
                    </span>
                </div>
            );
        }

        const currentReviewerName = managerWorkflow?.currentReviewerDisplayName ?? placeholderReviewerName;
        let nextReviewerName = managerWorkflow?.nextReviewerDisplayName ?? placeholderReviewerName;
        if (managerWorkflow?.currentReviewerDisplayName && !managerWorkflow?.nextReviewerDisplayName) {
            nextReviewerName = 'HR Administrator';
        }

        return (
            <div className="review-status">
                <span className="current-reviewer-status">
                    <span className="reviewer-text">
                        Current Reviewer&nbsp;
                    </span>
                    <span className={`reviewer-name ${placeholderClass}`}>
                        {currentReviewerName}
                    </span>
                </span>
                <span className="next-reviewer-status">
                    <span className="reviewer-text">
                        Next Reviewer&nbsp;
                    </span>
                    <span className={`reviewer-name ${placeholderClass}`}>
                        {nextReviewerName}
                    </span>
                </span>
            </div>
        );
    }

    private getSelectedReviewerNames(review: ReviewType): string {
        const { managerWorkflowStore } = this.props;
        const selected = managerWorkflowStore.selectedManagerWorkflows;
        if (!selected) {
            return '';
        }

        const reviewers: string[] = [];
        if (review === ReviewType.Current) {
            selected.forEach(workflow => {
                if (workflow.currentReviewerDisplayName) {
                    reviewers.push(workflow.currentReviewerDisplayName);
                }
            });
        } else if (review === ReviewType.Next) {
            selected.forEach(workflow => {
                if (workflow.nextReviewerDisplayName) {
                    reviewers.push(workflow.nextReviewerDisplayName);
                }
            });
        } else {
            selected.forEach(workflow => {
                if (workflow.approvedByUserDisplayName) {
                    reviewers.push(workflow.approvedByUserDisplayName);
                }
            });
        }

        const uniqueReviewers = Array.from(new Set(reviewers));
        if (uniqueReviewers.length > maxDisplayedReviewers) {
            const extras = uniqueReviewers.length - maxDisplayedReviewers;
            const reviewerText = uniqueReviewers.slice(0, maxDisplayedReviewers - 1).join(', ');
            return `${reviewerText}, and ${extras} more`;
        }
        return uniqueReviewers.join(', ');
    }

    private needsPlaceholder(managerWorkflow: MWF): boolean {
        return !managerWorkflow || !managerWorkflow.currentReviewerDisplayName ||
            !managerListSidePanelStore.allManagerIds.length;
    }
}

export default observer(ManagerWorkflow);